import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  CreateGuaranteeLetterClient,
  DeleteGuaranteeLetterClient,
  GetGuaranteeLetterClient,
  SearchGuaranteeLetterClient,
  UpdateGuaranteeLetterClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const createGuaranteeLetterClient = new CreateGuaranteeLetterClient(SERVICE_URL)
const deleteGuaranteeLetterClient = new DeleteGuaranteeLetterClient(SERVICE_URL)
const getGuaranteeLetterClient = new GetGuaranteeLetterClient(SERVICE_URL)
const searchGuaranteeLetterClient = new SearchGuaranteeLetterClient(SERVICE_URL)
const updateGuaranteeLetterClient = new UpdateGuaranteeLetterClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  excel: null,
  guaranteeletter: null,
  detail: null,
}

// Async Thunks
export const guaranteeletterGetAll = createAsyncThunk(
  'guaranteeletter/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchGuaranteeLetterClient.search(
        params.name,
        params.projectId,
        params.companyId,
        params.bankId,
        params.guaranteeLetterTypeId,
        params.guaranteeLetterStateId,
        params.startDate,
        params.endDate,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setGuaranteeLetterList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const guaranteeletterGetId = createAsyncThunk(
  'guaranteeletter/getById',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getGuaranteeLetterClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const guaranteeletterExcel = createAsyncThunk(
  'guaranteeletter/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchGuaranteeLetterClient.excel(
        params.name,
        params.projectId,
        params.companyId,
        params.bankId,
        params.guaranteeLetterTypeId,
        params.guaranteeLetterStateId,
        params.startDate,
        params.endDate,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const guaranteeletterCreate = createAsyncThunk(
  'guaranteeletter/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await createGuaranteeLetterClient.create(params)
      dispatch(setCreated(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const guaranteeletterUpdate = createAsyncThunk(
  'guaranteeletter/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateGuaranteeLetterClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

export const guaranteeletterDelete = createAsyncThunk(
  'guaranteeletter/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await deleteGuaranteeLetterClient.delete(params)
      dispatch(setDeleted(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const guaranteeletterSlice = createSlice({
  name: 'guaranteeletter',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setGuaranteeLetterList: (state, action) => {
      state.guaranteeletter = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setCreated: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setDeleted: (state) => {
      state.isDeleted = true
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('guaranteeletter/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setGuaranteeLetterList,
  setDetail,
  setExcel,
  setCreated,
  setDeleted,
  setRedirect,
  resetState,
} = guaranteeletterSlice.actions

// Selectors
export const selectGuaranteeLetterLoading = (state) => state.guaranteeletter.loading
export const selectGuaranteeLetterError = (state) => state.guaranteeletter.error
export const selectGuaranteeLetterList = (state) => state.guaranteeletter.guaranteeletter
export const selectGuaranteeLetterDetail = (state) => state.guaranteeletter.detail
export const selectGuaranteeLetterExcel = (state) => state.guaranteeletter.excel
export const selectGuaranteeLetterIsDeleted = (state) => state.guaranteeletter.isDeleted
export const selectGuaranteeLetterIsRedirect = (state) => state.guaranteeletter.isRedirect

export default guaranteeletterSlice.reducer
